import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import {
  getApiErrorMessage,
  listDepartments,
  listTeams,
  listTicketCategoryOptions,
  listTicketStatusOptions,
  listTickets,
} from '../../api/client';
import type { DepartmentSummary, TeamListItem, TicketCategoryRef, TicketListItem, TicketStatusRef } from '../../api/types';
import Badge from '../../components/Badge';
import ChannelIcon from '../../components/ChannelIcon';
import TicketLabelBadges from '../../components/TicketLabelBadges';
import { useAuth } from '../../contexts/AuthContext';
import { useTicketListLiveSync } from '../../hooks/useTicketListLiveSync';
import { canViewAllTicketsInList } from '../../utils/permissions';
import {
  formatStatus,
  priorityBadgeClass,
  priorityRowClass,
  statusBadgeClass,
} from '../../utils/ticket';
import { ticketCreatorName } from '../../utils/ticketDisplay';

const TICKET_PAGE_SIZE = 20;

type Props = {
  selectedTicketId?: string;
  compact?: boolean;
};

type QueueFilter = 'default' | 'assigned' | 'all';

function TicketQueueFilter({
  canViewAll,
  assignedToMe,
  onAssignedToMeChange,
}: {
  canViewAll: boolean;
  assignedToMe: boolean;
  onAssignedToMeChange: (assignedToMe: boolean) => void;
}) {
  return (
    <div className="form-group">
      <label>My queue</label>
      {canViewAll ? (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.35rem' }}>
          <input
            type="checkbox"
            checked={assignedToMe}
            onChange={(e) => onAssignedToMeChange(e.target.checked)}
          />
          Assigned to me
        </label>
      ) : (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.35rem' }}>
          <input
            type="checkbox"
            checked={!assignedToMe}
            onChange={(e) => onAssignedToMeChange(!e.target.checked)}
          />
          Show all
        </label>
      )}
    </div>
  );
}

export default function TicketListPanel({ selectedTicketId, compact = false }: Props) {
  const { user } = useAuth();
  const canViewAll = user ? canViewAllTicketsInList(user) : false;
  const [queueFilter, setQueueFilter] = useState<QueueFilter>('default');
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TICKET_PAGE_SIZE);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [statuses, setStatuses] = useState<TicketStatusRef[]>([]);
  const [categories, setCategories] = useState<TicketCategoryRef[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [departmentId, setDepartmentId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [ownershipDepartmentId, setOwnershipDepartmentId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const assignedToMe = useMemo(() => {
    if (queueFilter === 'assigned') return true;
    if (queueFilter === 'all') return false;
    return !canViewAll;
  }, [queueFilter, canViewAll]);

  function handleQueueFilterChange(nextAssignedToMe: boolean) {
    setQueueFilter(nextAssignedToMe ? 'assigned' : 'all');
  }

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const listFilters = useMemo(
    () => ({
      departmentId,
      statusId,
      categoryId,
      assignedToMe,
      teamId,
      ownershipDepartmentId,
      search: debouncedSearch,
    }),
    [
      departmentId,
      statusId,
      categoryId,
      assignedToMe,
      teamId,
      ownershipDepartmentId,
      debouncedSearch,
    ],
  );

  const [filtersForPage, setFiltersForPage] = useState(listFilters);
  if (filtersForPage !== listFilters) {
    setFiltersForPage(listFilters);
    setPage(1);
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [deptData, statusData, categoryData] = await Promise.all([
          listDepartments(),
          listTicketStatusOptions(),
          listTicketCategoryOptions(),
        ]);
        if (cancelled) return;
        setDepartments(deptData);
        setStatuses(statusData);
        setCategories(categoryData);
      } catch (err) {
        if (!cancelled) setError(getApiErrorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!departmentId) {
      setTeams([]);
      return;
    }
    let cancelled = false;
    void listTeams(departmentId)
      .then((teamData) => {
        if (!cancelled) setTeams(teamData);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [departmentId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listTickets({
        departmentId: departmentId || undefined,
        statusId: statusId || undefined,
        categoryId: categoryId || undefined,
        assignedToMe: assignedToMe || undefined,
        teamId: teamId || undefined,
        ownershipDepartmentId: ownershipDepartmentId || undefined,
        search: debouncedSearch.trim() || undefined,
        page,
      });
      setTickets(Array.isArray(result.items) ? result.items : []);
      setTotal(result.total);
      setPageSize(result.pageSize || TICKET_PAGE_SIZE);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [
    departmentId,
    statusId,
    categoryId,
    assignedToMe,
    teamId,
    ownershipDepartmentId,
    debouncedSearch,
    page,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const { newTicketIds, pendingNewCount, dismissNewBanner, scrollToFirstNew } =
    useTicketListLiveSync({
      user,
      filters: listFilters,
      tickets,
      setTickets,
      setTotal,
      page,
      pageSize,
      onRefetch: () => {
        void load();
      },
    });

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  function handleNewBannerClick() {
    if (page > 1) {
      setPage(1);
      dismissNewBanner();
      return;
    }
    scrollToFirstNew();
  }

  return (
    <>
      {!compact && (
        <div className="page-header">
          <div>
            <h1>Tickets</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link to="/tickets/new" className="btn btn-primary">
              New ticket
            </Link>
          </div>
        </div>
      )}

      {compact && (
        <div className="tickets-split-list-header">
          <h2>Tickets</h2>
          <Link to="/tickets/new" className="btn btn-primary btn-sm">
            New
          </Link>
        </div>
      )}

      {pendingNewCount > 0 && (
        <button type="button" className="ticket-list-new-banner" onClick={handleNewBannerClick}>
          {pendingNewCount === 1 ? '1 new ticket' : `${pendingNewCount} new tickets`}
        </button>
      )}

      <div className={`filters card card-body${compact ? ' filters--compact' : ''}`}>
        <div className="tickets-search-wrap">
          <span className="tickets-search-icon" aria-hidden>
            🔍
          </span>
          <input
            type="search"
            className="tickets-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={compact ? 'Search tickets…' : 'Search by name, email, phone, ticket ID, enrollment ID…'}
          />
          {search && (
            <button
              type="button"
              className="tickets-search-clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {!compact && (
          <>
            <div className="form-group">
              <label>Department</label>
              <select
                className="form-control"
                value={departmentId}
                onChange={(e) => {
                  setDepartmentId(e.target.value);
                  setTeamId('');
                }}
              >
                <option value="">All</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                className="form-control"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">All</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="form-control"
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <option value="">All</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {formatStatus(s)}
                  </option>
                ))}
              </select>
            </div>
            <TicketQueueFilter
              canViewAll={canViewAll}
              assignedToMe={assignedToMe}
              onAssignedToMeChange={handleQueueFilterChange}
            />
            {teams.length > 0 && (
              <div className="form-group">
                <label>Team</label>
                <select
                  className="form-control"
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                >
                  <option value="">All teams</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Active in dept</label>
              <select
                className="form-control"
                value={ownershipDepartmentId}
                onChange={(e) => setOwnershipDepartmentId(e.target.value)}
              >
                <option value="">Any</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        {compact && (
          <>
            <div className="form-group">
              <label>Status</label>
              <select
                className="form-control"
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <option value="">All</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {formatStatus(s)}
                  </option>
                ))}
              </select>
            </div>
            <TicketQueueFilter
              canViewAll={canViewAll}
              assignedToMe={assignedToMe}
              onAssignedToMeChange={handleQueueFilterChange}
            />
          </>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className={`card${compact ? ' tickets-split-list-table' : ''}`}>
        {loading ? (
          <div className="loading">Loading tickets…</div>
        ) : tickets.length === 0 && debouncedSearch.trim() ? (
          <div className="empty-state">No tickets found for &ldquo;{debouncedSearch.trim()}&rdquo;</div>
        ) : tickets.length === 0 ? (
          <div className="empty-state">No tickets match your filters.</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  {!compact && (
                    <>
                      <th>Priority</th>
                      <th>Category</th>
                      <th>Department</th>
                      <th>Assignee</th>
                      <th>Created by</th>
                      <th>Created</th>
                    </>
                  )}
                  {compact && <th>Priority</th>}
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => {
                  const isSelected = selectedTicketId === t.id;
                  return (
                    <tr
                      key={t.id}
                      data-ticket-id={t.id}
                      className={`${priorityRowClass(t.priority)}${
                        newTicketIds.has(t.id) ? ' ticket-row--new' : ''
                      }${isSelected ? ' ticket-row--selected' : ''}`}
                    >
                      <td>
                        <div className="ticket-title-cell">
                          <span className="ticket-title-row">
                            <NavLink
                              to={`/tickets/${t.id}`}
                              className={({ isActive }) => (isActive ? 'ticket-link--active' : undefined)}
                            >
                              {t.title}
                            </NavLink>
                          </span>
                          <span className="ticket-channel-meta">
                            <ChannelIcon code={t.channelType.code} />
                            {t.channelType.name}
                          </span>
                          {compact && (
                            <span className="ticket-creator-meta">By {ticketCreatorName(t)}</span>
                          )}
                          {t.labels.length > 0 && (
                            <div style={{ marginTop: '0.15rem' }}>
                              <TicketLabelBadges labels={t.labels} maxVisible={compact ? 1 : 2} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge label={t.status.name} className={statusBadgeClass(t.status)} />
                      </td>
                      {!compact && (
                        <>
                          <td>
                            <Badge label={t.priority} className={priorityBadgeClass(t.priority)} />
                          </td>
                          <td>{t.category.name}</td>
                          <td>{t.department.name}</td>
                          <td>
                            {t.assignedTo
                              ? `${t.assignedTo.firstName} ${t.assignedTo.lastName}`
                              : '—'}
                          </td>
                          <td>{ticketCreatorName(t)}</td>
                          <td>{new Date(t.createdAt).toLocaleDateString()}</td>
                        </>
                      )}
                      {compact && (
                        <td>
                          <Badge label={t.priority} className={priorityBadgeClass(t.priority)} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {total > 0 && (
          <div className="pagination staff-pagination ticket-list-pagination">
            <button
              type="button"
              className="pagination-page"
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </button>
            <span className="pagination-page active">{page}</span>
            <button
              type="button"
              className="pagination-page"
              disabled={page * pageSize >= total || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
            <p className="pagination-meta">
              Showing {rangeStart}–{rangeEnd} of {total}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
